import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { DecimalPipe, I18nPluralPipe, NgForOf } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { switchMap } from 'rxjs/operators';
import { PmcFiltersSidenavComponent, PmcFiltersSidenavInput, PmcFiltersSidenavOutput } from './pmc-filters-sidenav/pmc-filters-sidenav.component';
import { SidenavContentService } from '../shared/sidenav/sidenav-content/sidenav-content.service';
import { AppTableComponent, SelectedEvent } from '../shared/table/table.component';
import { EmptyCellDirective, NumberCellDirective } from '../shared/table/table.directive';
import { PropertyManagementCompany } from '../../../models/entities/property-management-company';
import { TableTypeNames } from '../../../services/column.service';
import { ColumnMap } from '../../../models/entities/column-map';
import { PropertyManagementCompanyService } from '../../../services/property-management-company/property-management-company.service';
import { ScrollService } from '../../../services/scroll.service';
import { GrowlerService } from '../../../services/growler.service';
import { I18nMap } from '../../../models/entities/i18n-map';
import { AvatarBlockModule } from '../shared/avatar-block/avatar-block.module';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import { EmptyStateModule } from '../shared/empty-state/empty-state.module';
import { RouterButtonValue } from '../../../models/entities/router-button-value';
import { AddressLinePipe } from '../../../pipes/format/address-line.pipe';

@UntilDestroy()
@Component({
    selector: 'app-pmc-list',
    templateUrl: './pmc-list.component.html',
    standalone: true,
    styleUrls: ['./pmc-list.component.scss'],
    imports: [
        AppTableComponent,
        MatSortModule,
        MatTableModule,
        EmptyCellDirective,
        I18nPluralPipe,
        DecimalPipe,
        NumberCellDirective,
        NgForOf,
        AvatarBlockModule,
        RouterLink,
        PageHeaderComponent,
        EmptyStateModule,
        MatButtonModule,
        MatIconModule,
        AddressLinePipe,
    ],
})
export class PmcListComponent implements OnInit, AfterViewInit
{
    @ViewChild(AppTableComponent) tableRef: AppTableComponent<PropertyManagementCompany>;
    @ViewChild(MatSort, { static: true }) columnSort: MatSort;

    routerAddButton: RouterButtonValue;
    hasData = false;

    readonly organizationsMapping: I18nMap = {
        '=0': 'No organizations',
        '=1': '1 organization',
        other: '# organizations',
    };
    readonly tableType = TableTypeNames.PMC_GROUPS;
    readonly columnMap:ColumnMap[] = [
        {key: 'mobile', title: 'Company', exportAs: 'company', isMobile: true},
        {key: 'name', title: 'Company', exportAs: 'company'},
        {key: 'emails', title: 'Emails', exportAs: 'emails'},
        {key: 'regions', title: 'Regions', exportAs: 'regions'},
        {key: 'unitsCount', title: 'Units', exportAs: 'units'},
        {key: 'membersCount', title: 'Members', exportAs: 'members'},
    ];

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _sidenav: SidenavContentService,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    ngOnInit() {
        this._scrollService.scrollToTop();

        this.routerAddButton = {
            label: 'Company',
            route: `/pmc/add`,
            ariaLabel: 'Add new company',
        };
    }

    ngAfterViewInit() {
        this.setupTable();
    }

    goToPmc(event: SelectedEvent<PropertyManagementCompany>) {
        return this._router.navigate(['..', event.source.id], { relativeTo: this._activatedRoute });
    }

    openFilters(filters: { [key: string]: any }) {
        const data: PmcFiltersSidenavInput = {
            filters,
        };

        this._sidenav.open(PmcFiltersSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: PmcFiltersSidenavOutput) => {
            if (result?.success) {
                return this._router.navigate([], {
                    relativeTo: this._activatedRoute,
                    queryParams: {
                        ...result.filters,
                    },
                });
            }
        });
    }

    private setupTable() {
        this.tableRef.connectDataTable$.pipe(
                switchMap(() => {
                    return this._propertyManagementCompanyService.getPropertyManagementCompanies(
                            this.tableRef.searchValue,
                            this.columnSort.active,
                            this.columnSort.direction,
                            this.tableRef.paginator.pageIndex + 1,
                            this.tableRef.paginator.pageSize,
                            this.tableRef.filters
                    );
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => this.tableRef.data$.next(res),
            error: () => {
                this.tableRef.loading = false;
                this._growler.oops('Unable to load property management companies');
            },
        });
    }
}
